import React, { useState, useRef, useEffect } from "react";
import { Camera, MapPin, Star, Briefcase, CheckCircle2, Calendar, X } from "lucide-react";
import { api, formatApiError, fileUrl, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StarRating, RatingBreakdown, ReputationBadge, ReputationProgress } from "@/components/StarRating";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({
    name: user.name || "", phone: user.phone || "", city: user.city || "", address: user.address || "",
    bio: user.bio || "", experience: user.experience || "", availability: user.availability || "",
    skills: user.skills || [], photo_url: user.photo_url || null,
    latitude: user.latitude, longitude: user.longitude,
  });
  const [skillInput, setSkillInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [breakdown, setBreakdown] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    api.get(`/users/${user.id}`).then(({ data }) => setBreakdown(data.rating_breakdown)).catch(() => {});
  }, [user.id]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, photo_url: data.url });
      toast.success("Foto diunggah");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const addSkill = () => {
    if (skillInput.trim() && !form.skills.includes(skillInput.trim())) {
      setForm({ ...form, skills: [...form.skills, skillInput.trim()] });
      setSkillInput("");
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/profile", form);
      setUser(data.user);
      toast.success("Profil berhasil disimpan.");
      setEdit(false);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const useGeo = () => {
    navigator.geolocation?.getCurrentPosition(
      (p) => { setForm({ ...form, latitude: p.coords.latitude, longitude: p.coords.longitude }); toast.success("Lokasi disimpan"); },
      () => toast.error("Gagal mendapatkan lokasi")
    );
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="profile-page">
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex flex-col items-center text-center">
          <div className="relative">
            <Avatar className="w-24 h-24 border-2 border-slate-100">
              <AvatarImage src={fileUrl(form.photo_url)} />
              <AvatarFallback className="bg-blue-100 text-blue-700 text-2xl font-bold">{user.name?.[0]}</AvatarFallback>
            </Avatar>
            {edit && (
              <>
                <button data-testid="upload-photo-btn" onClick={() => fileRef.current.click()} className="absolute bottom-0 right-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white shadow-lg"><Camera size={15} /></button>
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadPhoto} />
              </>
            )}
          </div>
          {!edit && (
            <>
              <h1 className="text-2xl font-extrabold mt-4 flex items-center gap-2">{user.name}{user.reputation && <ReputationBadge level={user.reputation} />}</h1>
              <p className="text-slate-500 flex items-center gap-1 mt-1"><MapPin size={15} className="text-blue-600" /> {user.city || "Lokasi belum diatur"}</p>
              <div className="mt-2"><StarRating value={user.rating_average} count={user.rating_count} /></div>
              <div className="mt-1.5"><ReputationProgress completed={user.completed_jobs} /></div>
            </>
          )}
        </div>

        {!edit ? (
          <>
            {breakdown && user.rating_count > 0 && (
              <div className="mt-5"><RatingBreakdown breakdown={breakdown} average={user.rating_average} count={user.rating_count} /></div>
            )}
            <div className="grid grid-cols-3 gap-3 mt-6">
              <Stat icon={Briefcase} value={user.jobs_posted} label="Diposting" />
              <Stat icon={CheckCircle2} value={user.completed_jobs} label="Selesai" />
              <Stat icon={Calendar} value={formatDate(user.created_at)} label="Bergabung" small />
            </div>
            {user.bio && <p className="text-slate-600 mt-5">{user.bio}</p>}
            {user.skills?.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-bold text-slate-500 uppercase mb-2">Keahlian</p>
                <div className="flex flex-wrap gap-2">{user.skills.map((s) => <span key={s} className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-sm">{s}</span>)}</div>
              </div>
            )}
            {(user.experience || user.availability) && (
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                {user.experience && <Detail label="Pengalaman" value={user.experience} />}
                {user.availability && <Detail label="Ketersediaan" value={user.availability} />}
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-slate-100 text-sm text-slate-500">
              <p>Email: {user.email}</p>
              <p>HP: {user.phone}</p>
            </div>
            <Button data-testid="edit-profile-btn" onClick={() => setEdit(true)} className="w-full mt-5 bg-blue-600 hover:bg-blue-700 rounded-lg">Edit Profil</Button>
          </>
        ) : (
          <div className="space-y-4 mt-6">
            <F label="Nama"><Input data-testid="edit-name" value={form.name} onChange={set("name")} /></F>
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Nomor HP"><Input data-testid="edit-phone" value={form.phone} onChange={set("phone")} /></F>
              <F label="Kota"><Input data-testid="edit-city" value={form.city} onChange={set("city")} placeholder="Medan" /></F>
            </div>
            <F label="Alamat"><Input data-testid="edit-address" value={form.address} onChange={set("address")} /></F>
            <F label="Bio"><Textarea data-testid="edit-bio" value={form.bio} onChange={set("bio")} rows={3} placeholder="Ceritakan tentang dirimu..." /></F>
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Pengalaman"><Input data-testid="edit-experience" value={form.experience} onChange={set("experience")} placeholder="3 tahun" /></F>
              <F label="Ketersediaan"><Input data-testid="edit-availability" value={form.availability} onChange={set("availability")} placeholder="Setiap hari" /></F>
            </div>
            <F label="Keahlian">
              <div className="flex gap-2">
                <Input data-testid="edit-skill-input" value={skillInput} onChange={(e) => setSkillInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSkill())} placeholder="Tambah keahlian" />
                <Button type="button" data-testid="add-skill-btn" onClick={addSkill} variant="outline" className="rounded-lg">Tambah</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {form.skills.map((s) => (
                  <span key={s} className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                    {s} <button onClick={() => setForm({ ...form, skills: form.skills.filter((x) => x !== s) })}><X size={13} /></button>
                  </span>
                ))}
              </div>
            </F>
            <Button data-testid="edit-geo-btn" variant="outline" onClick={useGeo} className="rounded-lg w-full text-sm">
              {form.latitude ? "Lokasi tersimpan ✓" : "Gunakan lokasi saya (untuk jarak)"}
            </Button>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setEdit(false)} className="rounded-lg flex-1">Batal</Button>
              <Button data-testid="save-profile-btn" onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700 rounded-lg flex-1">{busy ? "Menyimpan..." : "Simpan"}</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, value, label, small }) => (
  <div className="bg-slate-50 rounded-xl p-3 text-center">
    <Icon size={16} className="text-blue-600 mx-auto mb-1" />
    <p className={`font-bold text-slate-900 ${small ? "text-xs" : "text-lg"}`}>{value}</p>
    <p className="text-xs text-slate-400">{label}</p>
  </div>
);
const Detail = ({ label, value }) => (<div><p className="text-xs font-bold text-slate-500 uppercase">{label}</p><p className="text-slate-700">{value}</p></div>);
const F = ({ label, children }) => (<div><Label className="mb-1.5 block">{label}</Label>{children}</div>);
